import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetAgencyEmployeeDashboardListParams } from '@nxsys/store/slices/dashboard/types';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export enum AgencyEmployeeModalTypeEnum {
  TOTAL = 'TOTAL',
  PENDING_REGISTRATION = 'PENDING_REGISTRATION',
  REGISTERED = 'REGISTERED',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: AgencyEmployeeModalTypeEnum;
  agencyId: string;
};

const AgencyEmployeeModal: React.FC<Props> = ({
  isOpen,
  toggle,
  type,
  agencyId,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { employeeList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = employeeList;

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetAgencyEmployeeDashboardListParams) => {
    const newParams = {
      ...params,
      agencyIds: [agencyId],
      agencyRefs: [agencyId],
    };
    if (type === AgencyEmployeeModalTypeEnum.PENDING_REGISTRATION) {
      newParams.isPending = true;
    } else if (type === AgencyEmployeeModalTypeEnum.REGISTERED) {
      newParams.isRegisted = true;
    }
    dispatch(dashboardActions.getAgencyEmployeeDashboardList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const columns: ColumnsType<EmployeeRecordType> = [
    {
      key: 'agencyRef',
      title: 'Agency Ref',
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '_'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
    },
    {
      key: 'forename',
      title: 'Forename',
      dataIndex: 'forename',
    },
    {
      key: 'surname',
      title: 'Surname',
      dataIndex: 'surname',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
    },
    {
      key: 'mobile',
      title: 'Mobile',
      dataIndex: 'mobile',
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
      width: 140,
    },
    {
      key: 'setting',
      title: 'UTR No',
      dataIndex: 'setting',
      render: (value) => value?.UTRNo || '',
    },
  ];

  return (
    <AppModalContainer
      title={
        <div className="flex items-center gap-x-[8px]">
          <div>
            {type === AgencyEmployeeModalTypeEnum.PENDING_REGISTRATION
              ? 'Pending Registration'
              : type === AgencyEmployeeModalTypeEnum.REGISTERED
                ? 'Registered'
                : messages['common.totalEmployees']}
          </div>
          <div className="px-[12px] py-[2px] rounded-full bg-primary-50 border border-primary-500 text-primary-600 text-[14px]">
            {list?.length}
          </div>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200 }}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default AgencyEmployeeModal;
